<template>
  <div class="pay-qrcode">
    <p class="scan-tip">{{ text.payScanTip }}</p>
    <p class="countdown">
      {{ text.payCountdownLabel }}<span class="time">{{ countdownText }}</span>
    </p>
    <div class="qrcode-wrap">
      <!-- 支付二维码：由 qrcode.vue 依据支付链接实时生成（qrcode 为空时用占位图兜底）；
           未勾选协议时整体模糊处理，保证不可扫描 -->
      <div class="qrcode-inner" :class="{ 'is-locked': !agreed }">
        <!-- size 用 148 而非内区的 164：设计稿二维码图案 148、卡片 176
             （图案到卡片边缘 13~15px）。qrcode.vue@1.7.0 生成时 margin=0，
             图案铺满画布、不带静默区，差额由 .qrcode-inner 的居中留白补足 -->
        <qrcode-vue v-if="qrcode" :value="qrcode" :size="148" level="M" />
        <img v-else :src="placeholder" alt="支付二维码" />
      </div>
      <div v-if="!agreed" class="qrcode-mask">
        <img class="lock-icon" :src="lockIcon" alt="" />
        <p class="lock-tip">{{ text.agreeLockTip }}</p>
        <span class="lock-btn" @click="$emit('agree')">{{
          text.agreeBtn
        }}</span>
      </div>
    </div>
    <div class="agree-row" @click="$emit('toggle-agree')">
      <span class="checkbox" :class="{ checked: agreed }"></span>
      <span class="agree-text">
        {{ text.agreePrefix
        }}<span class="agree-name" @click.stop="$emit('view-protocol')">{{
          text.agreeName
        }}</span>
      </span>
    </div>
  </div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import { PRO_PLAN_TEXT } from "@/consts/proPlan";

/**
 * 支付二维码区（二维码 + 倒计时 + 协议勾选 + 未勾选锁定遮罩）
 * 二维码由 qrcode.vue 依据后端返回的支付链接实时生成。
 */
export default {
  name: "PayQrcode",
  components: { QrcodeVue },
  props: {
    qrcode: {
      type: String,
      default: "",
    },
    agreed: {
      type: Boolean,
      default: false,
    },
    // 剩余毫秒
    remainMs: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      text: PRO_PLAN_TEXT,
      // 支付链接未就绪时的占位图
      placeholder: require("@/assets/images/pro-plan/tex-free.png"),
      // 锁图标（设计稿切图：白色实心锁 + 灰色锁孔）
      lockIcon: require("@/assets/images/pro-plan/plan/pay-lock-icon.png"),
    };
  },
  computed: {
    countdownText() {
      const total = Math.max(0, Math.floor(this.remainMs / 1000));
      const m = Math.floor(total / 60);
      const s = total % 60;
      const mm = m < 10 ? "0" + m : "" + m;
      const ss = s < 10 ? "0" + s : "" + s;
      return `${mm}:${ss}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.pay-qrcode {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.scan-tip {
  margin: 0;
  font-size: 16px;
  color: #62717e;
}
.countdown {
  margin: 0;
  font-size: 16px;
  color: #62717e;
  .time {
    font-weight: 700;
    color: #f56c6c;
  }
}
// 二维码卡片：白底 + 浅灰描边，二维码四周留白色内边距（设计稿：卡片 176，边框 1，内边距 5，码 164）
.qrcode-wrap {
  position: relative;
  width: 176px;
  height: 176px;
  padding: 5px;
  box-sizing: border-box;
  margin: 10px 0;
  background: #fff;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  overflow: hidden;
}
.qrcode-inner {
  width: 100%;
  height: 100%;
  // 圆角 + 裁切：把二维码的模糊扩散限制在内层范围内，
  // 否则 filter 的模糊会外溢到外层 padding，盖住卡片那圈白色内边距
  border-radius: 6px;
  overflow: hidden;
  // 画布按设计稿尺寸居中，四周留白即为二维码的「静默区」
  // （图案 148 居中于 164 内区 → 内留白 8 + 卡片 5 + 描边 1 = 14px，与设计稿 13~15 一致）
  display: flex;
  align-items: center;
  justify-content: center;
  // 消除子元素的行内间隙（qrcode.vue 渲染的 canvas 外层还有一层 div）
  font-size: 0;
  line-height: 0;
  // canvas 由 qrcode.vue 渲染，属于子组件内部节点，需用 ::v-deep 穿透 scoped。
  // 不给 canvas 设 width/height:100% —— 那会把 148 的画布拉伸到 164，静默区就没了
  ::v-deep canvas {
    display: block;
    width: 148px;
    height: 148px;
  }
  > img {
    display: block;
    width: 100%;
    height: 100%;
  }
  // 未勾选协议：模糊作用于二维码本体，由本层裁切 → 外圈白边保持清晰完全不糊
  // （略放大，避免模糊把边缘拉出透明缺口）
  &.is-locked {
    ::v-deep canvas,
    > img {
      filter: blur(2px);
      transform: scale(1.06);
    }
  }
}
// 遮罩：只覆盖二维码区域（不含外侧白色内边距），与设计稿一致
.qrcode-mask {
  position: absolute;
  left: 5px;
  top: 5px;
  width: 164px;
  height: 164px;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
// 锁图标：设计稿切图（白色实心锁 + 灰色锁孔）
.lock-icon {
  width: 24px;
  height: 24px;
  display: block;
  margin-bottom: 6px;
}
.lock-tip {
  margin: 0;
  font-size: 12px;
  color: #fff;
}
.lock-btn {
  margin-top: 8px;
  padding: 4px 8px;
  font-size: 12px;
  color: #333;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}
.agree-row {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  .checkbox {
    width: 14px;
    height: 14px;
    border: 1px solid #d7d7d7;
    border-radius: 50%;
    box-sizing: border-box;
    flex-shrink: 0;
    &.checked {
      border-color: #0fa87b;
      background: #0fa87b;
      position: relative;
      &::after {
        content: "";
        position: absolute;
        left: 4px;
        top: 1px;
        width: 3px;
        height: 7px;
        border: 1px solid #fff;
        border-top: none;
        border-left: none;
        transform: rotate(45deg);
      }
    }
  }
  .agree-text {
    font-size: 12px;
    color: #62717e;
  }
  .agree-name {
    color: #0fa87b;
  }
}
</style>
